import { useEffect, useState } from 'react';
import type { KeycloakEventInfo, KeycloakEventPage } from '../api/types';

const CATEGORY_COLORS: Record<string, string> = {
  login: '#2ba05a',
  logout: '#3b5bd6',
  account: '#c98a1a',
  error: '#b3423a',
  other: '#8a918d',
};

function eventTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

interface KeycloakEventFeedProps {
  load: (first: number, max: number) => Promise<KeycloakEventPage>;
  pageSize: number;
  showUser?: boolean;
  emptyLabel?: string;
}

export function KeycloakEventFeed({
  load,
  pageSize,
  showUser = false,
  emptyLabel = 'No Keycloak events.',
}: KeycloakEventFeedProps) {
  const [stack, setStack] = useState<number[]>([0]);
  const [page, setPage] = useState<KeycloakEventPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const first = stack[stack.length - 1];

  useEffect(() => {
    let active = true;
    setLoading(true);
    load(first, pageSize)
      .then((result) => {
        if (!active) return;
        setPage(result);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setPage(null);
        setError(cause instanceof Error ? cause.message : String(cause));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [load, first, pageSize]);

  const items = page?.items ?? [];
  const gridClass = `events-grid${showUser ? ' events-grid-user' : ''}`;

  return (
    <>
      {error && <div className="error-banner">{error}</div>}

      <div className="table-scroll">
        <div className={`${gridClass} events-grid-head`}>
          <div>Time</div>
          <div>Event</div>
          {showUser && <div>User</div>}
          <div>Client</div>
          <div>IP address</div>
        </div>
        {loading && <div className="empty-state">Loading events…</div>}
        {!loading &&
          items.map((event) => (
            <EventRow
              key={event.id}
              event={event}
              gridClass={gridClass}
              showUser={showUser}
              open={expanded === event.id}
              onToggle={() => setExpanded(expanded === event.id ? null : event.id)}
            />
          ))}
        {!loading && !error && items.length === 0 && (
          <div className="empty-state">{emptyLabel}</div>
        )}
      </div>

      <div className="pager">
        <div className="pager-info mono">
          Page {stack.length} · {items.length} {items.length === 1 ? 'event' : 'events'}
        </div>
        <div className="pager-actions">
          <button
            className="btn-secondary"
            disabled={stack.length === 1 || loading}
            onClick={() => {
              setExpanded(null);
              setStack((current) => current.slice(0, -1));
            }}
          >
            Previous
          </button>
          <button
            className="btn-secondary"
            disabled={!page?.hasMore || loading}
            onClick={() => {
              setExpanded(null);
              setStack((current) =>
                page ? [...current, page.nextFirst] : current,
              );
            }}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}

interface EventRowProps {
  event: KeycloakEventInfo;
  gridClass: string;
  showUser: boolean;
  open: boolean;
  onToggle: () => void;
}

function EventRow({ event, gridClass, showUser, open, onToggle }: EventRowProps) {
  const color = CATEGORY_COLORS[event.category] ?? CATEGORY_COLORS.other;
  return (
    <>
      <div
        className={`${gridClass} events-grid-row${open ? ' open' : ''}`}
        onClick={onToggle}
      >
        <div className="mono muted">{eventTime(event.occurredAt)}</div>
        <div className="event-type">
          <span className="event-dot" style={{ background: color }} />
          <span className="event-type-name mono" style={{ color }}>
            {event.type}
          </span>
        </div>
        {showUser && (
          <div className="event-user">
            {event.username ? (
              <>
                <div className="event-user-name">{event.name ?? event.username}</div>
                <div className="event-user-sub mono">{event.username}</div>
              </>
            ) : (
              <span className="muted">{event.userId ? 'Unknown user' : '—'}</span>
            )}
          </div>
        )}
        <div className="mono muted">{event.clientId ?? '—'}</div>
        <div className="mono muted">{event.ipAddress ?? '—'}</div>
      </div>
      {open && (
        <div className="event-detail-panel">
          <div className="event-detail-grid">
            {event.error && (
              <div>
                <div className="field-label">Error</div>
                <span className="mono event-error">{event.error}</span>
              </div>
            )}
            {event.sessionId && (
              <div>
                <div className="field-label">Session</div>
                <span className="mono">{event.sessionId}</span>
              </div>
            )}
            {event.userId && (
              <div>
                <div className="field-label">User id</div>
                <span className="mono">{event.userId}</span>
              </div>
            )}
            {event.email && (
              <div>
                <div className="field-label">Email</div>
                <span className="mono">{event.email}</span>
              </div>
            )}
            {event.details.map((detail) => (
              <div key={detail.key}>
                <div className="field-label">{detail.key}</div>
                <span className="mono">{detail.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
